import { useLazyQuery } from 'react-apollo';
import {
  GET_PROVIDER_ISBOOKED_AUDIT_HISTORY,
  GET_CLIENT_ISBOOKED_AUDIT_HISTORY
} from 'api/graphql/v2/queries/isBookedAudit';

export type AuditVariant = 'provider' | 'client';

export interface IIsBookedAuditRow {
  id: number;
  availabilityId?: number | null;
  // provider-only
  providerTemplateId?: number;
  providerId?: number;
  providerName?: string;
  // client-only
  clientId?: number;
  clientName?: string;
  hasNonBTBooking?: boolean;
  previousHasNonBTBooking?: boolean;
  // shared
  dayOfWeek: number;
  startTime?: string;
  endTime?: string;
  opensOn?: string | null;
  previousOpensOn?: string | null;
  previousValue: boolean;
  newValue: boolean;
  action: string;
  actionBy: string;
  eventId?: number | null;
  correlationId?: string | null;
  changedAt: string;
}

interface ProviderResult {
  getProviderIsBookedAuditHistory: IIsBookedAuditRow[];
}
interface ClientResult {
  getClientIsBookedAuditHistory: IIsBookedAuditRow[];
}

/**
 * Lazy hook for the isBookedBlock audit history. Pass the variant ('provider'
 * or 'client'); call `loadAuditHistory(availabilityId)` when the modal opens.
 * Both lazy queries are declared so hook order stays stable; only the one
 * matching the variant is triggered and read.
 */
export const useIsBookedAuditHistory = (variant: AuditVariant) => {
  const [
    loadProvider,
    { data: providerData, loading: providerLoading, error: providerError }
  ] = useLazyQuery<ProviderResult>(GET_PROVIDER_ISBOOKED_AUDIT_HISTORY, {
    fetchPolicy: 'no-cache'
  });
  const [
    loadClient,
    { data: clientData, loading: clientLoading, error: clientError }
  ] = useLazyQuery<ClientResult>(GET_CLIENT_ISBOOKED_AUDIT_HISTORY, {
    fetchPolicy: 'no-cache'
  });

  const isClient = variant === 'client';

  return {
    loadAuditHistory: (availabilityId: number) =>
      isClient
        ? loadClient({ variables: { availabilityId } })
        : loadProvider({ variables: { availabilityId } }),
    auditRows: isClient
      ? clientData?.getClientIsBookedAuditHistory ?? []
      : providerData?.getProviderIsBookedAuditHistory ?? [],
    isLoadingAudit: isClient ? clientLoading : providerLoading,
    auditError: isClient ? clientError : providerError
  };
};

/**
 * Backwards-compatible provider-only hook.
 */
export const useProviderIsBookedAuditHistory = () =>
  useIsBookedAuditHistory('provider');
